/**
 * @file AgencyLanding.jsx
 * @description Marketing landing page for agency prospects. Showcases the platform
 *   value proposition, features, and a CTA to sign up as an agency.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/AgencyLanding
 */
// ─── Marketing landing page for agency prospects ──────────────────────────────
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { T } from "../theme";
import { getPlans, getStarterPromoDates } from "../lib/plans";
import { loadAllConfigs } from "../lib/configService";
import ProductShowcase from "./ProductShowcase";
import AgencyProPreview from "./AgencyProPreview";
import AgencyBoostsPreview, { creditPriceRange } from "./AgencyBoostsPreview";
import { getBoostProducts, getCreditPacks } from "../lib/boostService";
import { ProofChips, FlowSteps, DifferentialGrid, PartnerCard, IndeedVsAbacases, FounderCard, OutsideFloridaNote, agencyChips } from "./AgencyVisuals";
import CasePromotion from "./CasePromotion";
import { proBreakEvenCases, isWorkforceNamed, getAgencyPricing } from "../lib/agencyPricing";
import PublicFooter from "./PublicFooter";
import ContactModal from "./ContactModal";
import { UsersIcon, CheckIcon, SearchIcon, BriefcaseIcon, MapPinIcon, MessageIcon, CalendarIcon, TrendingUpIcon } from "./Icons";
import { usePageSeo } from "../lib/seo";
import StickyMobileCta from "./StickyMobileCta";
import { HeroCtaButton } from "./HeroAlertsCta";
import { useFaqJsonLd } from "../lib/seoSchema";
import { useSiteSchema } from "../lib/seoSchema";
import { proTeamLogins } from "../lib/entitlements";

// ── Design tokens local to this page ─────────────────────────────────────────
const D = {
  navy:       "#0F1024",
  navyMid:    "#1E2140",
  accent:     T.indigo, // brand indigo is the one primary/CTA color; green is kept for status and money only
  accentGlow: "rgba(79,70,229,0.14)",
  green:      T.green,
  border:     "rgba(226,232,240,0.8)",
  textMid:    "#475569",
  textLight:  T.textSubtle, // theme token, 6.7:1 on white (the old slate #94A3B8 was 2.56:1)
  radius:     14,
};

// ── Post -> match -> hire flow ────────────────────────────────────────────────
const FLOW = [
  { Icon: BriefcaseIcon, n: 1 }, { Icon: SearchIcon, n: 2 }, { Icon: UsersIcon, n: 3 }, { Icon: CheckIcon, n: 4 },
];

// ── Plan feature details — built dynamically so opening counts always match config ─
// `workforce` adds the ABAcases Workforce bullet to both plans; it is false until Workforce is released
// (lib/agencyPricing isWorkforceNamed, platform_config workforce_enabled).
function buildPlanFeatures(starterIncluded, proCases, t, outreach = {}, teamLogins = 3, workforce = null) {
  const s = t ? (k, opt) => t(k, opt) : (k) => k;
  // workforce: null until Workforce is released, then { starter, pro } = people each plan can track.
  const wf = (n) => (workforce ? [s('agencyLanding.planWorkforce', { n })] : []);
  return {
    starter: [
      t ? t('agencyLanding.planStarterCases', { count: starterIncluded }) : `Includes ${starterIncluded} opening`,
      s('agencyLanding.planStarterPipeline'),
      s('agencyLanding.planStarterAlerts'),
      s('agencyLanding.planStarterMessaging'),
      s('agencyLanding.planStarterJournal'),
      s('agencyLanding.planStarterAnalytics'),
      s('agencyLanding.planStarterVerified'),
      s('agencyLanding.planStarterMap'),
      ...wf(workforce?.starter),
    ],
    pro: [
      t ? t('agencyLanding.planProCases', { count: proCases }) : `${proCases} active opening${proCases === 1 ? "" : "s"}`,
      s('agencyLanding.planProTeam', { count: teamLogins, others: Math.max(1, teamLogins - 1) }),
      s('agencyLanding.planProAnalytics'),
      s('agencyLanding.planProSavedSearches'),
      outreach.credits && outreach.range
        ? t('agencyLanding.planProOutreachCost', { count: outreach.credits, min: outreach.credits * outreach.range.min, max: outreach.credits * outreach.range.max })
        : s('agencyLanding.planProOutreach'),
      s('agencyLanding.planProCsv'),
      s('agencyLanding.planProEverything'),
      ...wf(workforce?.pro),
    ],
  };
}

// Capability strip — what the tool actually does, teased right under the hero
// (the full features section + dashboard showcase expand on these below).
const STATS = [
  { Icon: MapPinIcon,    valueKey: "agencyLanding.stat1Value", labelKey: "agencyLanding.stat1Label" },
  { Icon: MessageIcon,   valueKey: "agencyLanding.stat2Value", labelKey: "agencyLanding.stat2Label" },
  { Icon: CalendarIcon,  valueKey: "agencyLanding.stat3Value", labelKey: "agencyLanding.stat3Label" },
  { Icon: TrendingUpIcon, valueKey: "agencyLanding.stat4Value", labelKey: "agencyLanding.stat4Label" },
];

function PriceCard({ plan, features, annual, onSelect, extraSlotPrice, breakEven, breakEvenAfter }) {
  const { t, i18n } = useTranslation();
  const [hovered, setHovered] = useState(false);
  // getPlans() uses mo/annualMo — support both field name conventions
  const monthlyPrice = plan.id === "starter" ? (extraSlotPrice || plan.mo || 0) : (plan.mo ?? plan.price ?? 0);
  const annualPrice  = plan.annualMo ?? plan.annualPrice ?? monthlyPrice;
  const price = annual ? annualPrice : monthlyPrice;
  const isPopular = plan.popular;
  const isEs = String(i18n.language || "").toLowerCase().startsWith("es");
  const promoDates = getStarterPromoDates();
  const { isPromoActive } = promoDates;
  const endDateStr = isEs ? promoDates.endDateStrEs : promoDates.endDateStr;
  const resumeDateStr = isEs ? promoDates.resumeDateStrEs : promoDates.resumeDateStr;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        background: isPopular ? "linear-gradient(180deg, #FFFFFF 0%, #F8F7FF 100%)" : "#fff",
        border: `2px solid ${isPopular ? D.accent : hovered ? D.accent + "66" : D.border}`,
        borderRadius: D.radius + 2,
        padding: "32px 28px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        transition: "all 0.18s ease",
        boxShadow: isPopular
          ? `0 22px 70px rgba(79,70,229,0.18), 0 6px 20px rgba(0,0,0,0.08)`
          : hovered ? "0 8px 32px rgba(15,23,42,0.10)" : "0 1px 4px rgba(15,23,42,0.06)",
        transform: isPopular ? "scale(1.02)" : "scale(1)",
        flex: 1,
      }}
    >
      {isPopular && (
        <div style={{
          position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)",
          background: T.gradientIndigo, color: "#fff",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
          borderRadius: 100, padding: "4px 14px",
          fontFamily: "'JetBrains Mono', monospace",
          whiteSpace: "nowrap",
          boxShadow: "0 2px 8px rgba(79,70,229,0.35)",
        }}>
          {t('billing.mostPopular')}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          padding: "6px 12px",
          borderRadius: 999,
          background: isPopular ? "rgba(79,70,229,0.12)" : "#F8FAFC",
          border: `1px solid ${isPopular ? D.accent + "55" : D.border}`,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: "50%",
            background: isPopular ? D.accent : D.textLight,
            flexShrink: 0,
          }} />
          <div style={{
            fontSize: 12, fontWeight: 800, letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: D.navy,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {plan.label}
          </div>
        </div>
        {isPopular && (
          <div style={{ fontSize: 12, fontWeight: 600, color: D.textMid, marginBottom: 10 }}>
            {breakEven
              ? (breakEvenAfter && breakEvenAfter !== breakEven
                  ? t('agencyLanding.bestForGrowingPromo', { n: breakEven, after: breakEvenAfter })
                  : t('agencyLanding.bestForGrowing', { n: breakEven }))
              : t('agencyLanding.bestForGrowingFallback')}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontSize: 42, fontWeight: 800, color: D.navy, lineHeight: 1 }}>
            ${price}
          </span>
          <span style={{ fontSize: 13, color: D.textLight }}>
            {t('agencyLanding.perMonth', '/ mo')}
          </span>
        </div>
        {annual && plan.id !== "starter" && (
          <div style={{ fontSize: 11, color: T.green, fontWeight: 600, marginTop: 4 }}>
            {t('agencyLanding.billedAnnually', { amount: (monthlyPrice - annualPrice) * 12 })}
          </div>
        )}
        {!annual && plan.id !== "starter" && annualPrice < monthlyPrice && (
          <div data-testid="pro-annual-line" style={{ fontSize: 12, color: D.textMid, fontWeight: 600, marginTop: 4 }}>
            {t(extraSlotPrice > 0 ? 'agencyLanding.orAnnualExtras' : 'agencyLanding.orAnnual', { price: annualPrice, extra: extraSlotPrice })}
          </div>
        )}
        {plan.id === "starter" ? (
          <>
          {extraSlotPrice > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: D.accent, fontWeight: 600 }}>
              {t('agencyLanding.extraSlots', { price: extraSlotPrice })}
            </div>
          )}
          {isPromoActive && (
            <div style={{ marginTop: 10 }}>
              <div style={{
                display: "inline-flex", alignItems: "center",
                background: T.greenLight, border: `1px solid ${T.green}55`,
                borderRadius: 100, padding: "5px 12px",
              }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: T.green }}>
                  {t('agencyLanding.starterPromoBadge', { count: plan.cases, endDate: endDateStr })}
                </span>
              </div>
              <div style={{ marginTop: 6, fontSize: 14, color: D.textLight, lineHeight: 1.5 }}>
                {t('agencyLanding.starterPromoDetail', { price: monthlyPrice, count: plan.included, resumeDate: resumeDateStr })}
              </div>
              <div style={{ marginTop: 4, fontSize: 14, color: D.textLight, lineHeight: 1.5 }}>
                {t('agencyLanding.starterAfterTrial', { endDate: endDateStr })}
              </div>
            </div>
          )}
          </>
        ) : (
          <>
            <div style={{
              marginTop: 10, fontSize: 12,
              color: D.textLight,
            }}>
              {plan.cases === Infinity ? t('agencyLanding.unlimitedListings') : t('agencyLanding.upToListings', { count: plan.cases })}
            </div>
            {plan.cases !== Infinity && extraSlotPrice > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: D.accent, fontWeight: 600 }}>
                {t('agencyLanding.extraSlots', { price: extraSlotPrice })}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ flex: 1, marginBottom: 28 }}>
        {features.map((f, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            marginBottom: 11,
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%",
              background: isPopular ? D.accentGlow : T.greenLight,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginTop: 1,
            }}>
              <CheckIcon size={10} color={isPopular ? D.accent : T.green} />
            </div>
            <span style={{ fontSize: 13, color: D.textMid, lineHeight: 1.4 }}>{f}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onSelect}
        style={{
          width: "100%", padding: "14px 0",
          borderRadius: 12,
          border: `1.5px solid ${D.accent}`,
          background: T.gradientIndigo,
          color: "#fff",
          fontSize: 14, fontWeight: 800,
          fontFamily: "'Inter', sans-serif",
          cursor: "pointer",
          transition: "all 0.15s",
          boxShadow: isPopular ? "0 10px 24px rgba(79,70,229,0.28)" : "0 8px 18px rgba(79,70,229,0.22)",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 12px 28px rgba(79,70,229,0.34)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = isPopular ? "0 10px 24px rgba(79,70,229,0.28)" : "0 8px 18px rgba(79,70,229,0.22)";
        }}
      >
        {t('agencyLanding.getStarted', 'Get Started')}
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
// ── FAQ ───────────────────────────────────────────────────────────────────────
// Feeds useFaqJsonLd() for SEO structured data (Google indexes the prerendered
// English). Rendered cards pull display text from t() via index keys
// (agencyLanding.faq{n}Q / faq{n}A). Pricing/slot numbers are injected from admin
// config (getPlans -> platform_config) — never hardcode them here.
// Example sizes for the pricing example line (the prices come from config; these are just the illustrated counts).
const EXAMPLE_STARTER_OPENINGS = 3;
const EXAMPLE_PRO_OPENINGS = 12;

const AGENCY_FAQ_KEYS = ["faq1", "faq2", "faq4", "faq5", "faq6", "faq7", "faq8", "faq9"];

/** FAQ entries for the page and its JSON-LD. Text comes from the locale files only (English for JSON-LD,
 *  per this page's SEO convention); the page renders the same keys in the visitor's language. */
function buildAgencyFaqs(vars) {
  const en = i18n.getFixedT("en");
  const opts = { ...vars, price: vars.starterSlotPrice, endDate: vars.endDateStr, context: vars.isPromoActive ? undefined : "promoEnded" };
  return AGENCY_FAQ_KEYS.map((key) => ({ key, q: en(`agencyLanding.${key}Q`), a: en(`agencyLanding.${key}A`, opts) }));
}

export default function AgencyLanding({ onSignUp, embedded = false }) {
  const { t, i18n } = useTranslation();
  const [annual, setAnnual] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false); // 10+ openings: contact form prefilled
  const [partnerOpen, setPartnerOpen] = useState(false); // Founding Partner application (contact modal)
  const [openFaq, setOpenFaq] = useState(0); // FAQ accordion — verification FAQ open by default
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  const [, setConfigLoaded] = useState(false);
  const [platformConfig, setPlatformConfig] = useState(null); // boost costs and credit packs (AgencyBoostsPreview)
  // Extra case-slot price is plan-differentiated (Starter > Pro). Keep both.
  const [slotPriceStarter, setSlotPriceStarter] = useState(29);
  const [slotPricePro,     setSlotPricePro]     = useState(15);
  const [annualDiscountPct, setAnnualDiscountPct] = useState(20);

  // Standalone (/aba-agencies) owns the head incl. canonical + OG; embedded as a
  // HomePage section it's a no-op (the host page owns the head).
  usePageSeo(embedded ? {} : {
    title: "Post ABA Cases and Hire RBTs & BCBAs | ABAcases",
    description: "Post ABA cases on a platform built only for ABA. Provider credential checks, NPI-verified agencies, and the full hiring pipeline in one place.",
    canonical: "/aba-agencies",
  });
  useSiteSchema(!embedded); // global Organization + WebSite (standalone only)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    // Load admin-configurable plan data (prices, limits, trial days) from Supabase
    loadAllConfigs().then(cfg => {
      const getCfg = (k) => cfg instanceof Map ? cfg.get(k) : cfg?.[k];
      const legacySlot = Number(getCfg("extra_case_slot_price") ?? 9);
      setSlotPriceStarter(Number(getCfg("extra_case_slot_price_starter") ?? legacySlot ?? 29));
      setSlotPricePro(Number(getCfg("extra_case_slot_price_pro") ?? legacySlot ?? 15));
      setAnnualDiscountPct(Number(getCfg("plan_annual_discount_pct") ?? 20));
      setPlatformConfig(cfg);
      setConfigLoaded(true);
    }).catch(() => setConfigLoaded(true));
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const sectionPad = isMobile ? "56px 16px" : "80px 48px";

  // Plan values — re-evaluated after loadAllConfigs() triggers a re-render
  const plans       = getPlans();
  const starterPlan = plans.find(p => p.id === "starter");
  const proPlan     = plans.find(p => p.id === "pro");
  const starterCases = starterPlan?.cases ?? 2;           // free promo openings
  const starterMo       = slotPriceStarter;               // Starter IS the extra-opening subscription: one price (extra_case_slot_price_starter)
  const starterIncluded = starterPlan?.included ?? 1;     // openings the Starter price includes
  const proCases     = proPlan?.cases    ?? 10;
  const proMo        = proPlan?.mo       ?? 149;
  const proAnnual    = proPlan?.annualMo ?? 119;
  const creditRange = creditPriceRange(getCreditPacks(platformConfig));
  // Team logins on Pro: the same Admin setting the server's seat limit reads (lib/entitlements).
  const teamLogins = proTeamLogins((k) => (platformConfig instanceof Map ? platformConfig.get(k) : platformConfig?.[k]));
  const agencyPricing = getAgencyPricing(i18n.language);
  const planFeatures = buildPlanFeatures(starterIncluded, proCases, t, { credits: getBoostProducts(platformConfig).direct_outreach?.credits, range: creditRange }, teamLogins, isWorkforceNamed() ? { starter: agencyPricing.workforceMaxStarter, pro: agencyPricing.workforceMaxPro } : null);
  const isEsLocale = String(i18n.language || "").toLowerCase().startsWith("es");
  const promoDates = getStarterPromoDates();
  const promoActiveNow = promoDates.isPromoActive;
  // Pro is cheaper than stacking Starter slots from this many active openings; the launch offer's free ones count.
  const proBreakEven = proBreakEvenCases(slotPriceStarter, proMo, proCases, promoActiveNow ? starterCases : 0);
  const proBreakEvenAfter = proBreakEvenCases(slotPriceStarter, proMo, proCases);
  // JSON-LD (buildAgencyFaqs) stays English per this page's SEO convention —
  // use the plain endDateStr there. On-page copy uses the locale-aware one.
  const endDateStr = promoDates.endDateStr;
  const endDateStrLocalized = isEsLocale ? promoDates.endDateStrEs : promoDates.endDateStr;
  const startDateStrLocalized = isEsLocale ? promoDates.resumeDateStrEs : promoDates.resumeDateStr; // first day of the new plans
  // FAQ pricing/slot values come from admin config above — never hardcoded
  const faqVars     = { starterCases, proCases, proMo, proAnnual, starterSlotPrice: slotPriceStarter, price: slotPriceStarter, starterMo, starterIncluded, proExtra: slotPricePro, startDate: promoDates.resumeDateStr, endDateStr, endDate: endDateStr, isPromoActive: promoActiveNow,
    teamLogins, teammates: Math.max(1, teamLogins - 1) };
  const agencyFaqs  = buildAgencyFaqs(faqVars);
  useFaqJsonLd("faq-jsonld-agency-landing", agencyFaqs);

  return (
    <div style={embedded
      ? { fontFamily: "'Inter', 'DM Sans', sans-serif" }
      : { flex: 1, overflowY: "auto", background: "#F8FAFC", fontFamily: "'Inter', 'DM Sans', sans-serif" }}>

      {/* ══ HERO (suppressed when embedded as a HomePage section) ════ */}
      {!embedded && (
      <div style={{
        background: `linear-gradient(135deg, ${D.navy} 0%, ${D.navyMid} 60%, #1a1f4a 100%)`,
        padding: isMobile ? "48px 16px 44px" : "72px 48px 60px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -80, left: "20%", width: 400, height: 400, borderRadius: "50%", background: "rgba(79,70,229,0.18)", filter: "blur(80px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, right: "15%", width: 300, height: 300, borderRadius: "50%", background: "rgba(139,92,246,0.15)", filter: "blur(60px)", pointerEvents: "none" }} />

        <div style={{ position: "relative", maxWidth: 760, margin: "0 auto" }}>
          <div style={{ display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6ee7b7", background: "rgba(255,255,255,0.08)", borderRadius: 999, padding: "5px 14px", marginBottom: 16 }}>{t('agencyLanding.heroEyebrow')}</div>
          <h1 style={{ fontSize: "clamp(36px, 5vw, 58px)", fontWeight: 900, color: "#fff", lineHeight: 1.12, letterSpacing: "-0.03em", margin: "0 0 16px" }}>
            {t('agencyLanding.heroTitle')}
          </h1>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.72)", lineHeight: 1.6, maxWidth: 520, margin: "0 auto 26px" }}>
            {t('agencyLanding.heroTaglineShort')}
          </p>
          {/* Desktop: no hero button (owner, 2026-10-19), joining is the top bar's button and the plan cards below.
              Phones: the top bar's button is only 29px tall, so the hero carries the same action (same label and handler as the sticky bar). */}
          {isMobile && onSignUp && (
            <div style={{ margin: "0 0 18px" }}>
              <HeroCtaButton testId="hero-post-case" label={promoActiveNow ? t('agencyPricing.cta') : t('agencyPricing.ctaPaid')} onClick={onSignUp} isMobile />
            </div>
          )}
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)" }}>
            {promoActiveNow
              ? t('agencyLanding.heroFreeNote', { cases: starterCases, price: slotPriceStarter, endDateShort: isEsLocale ? promoDates.endDateShortStrEs : promoDates.endDateShortStr })
              : t('agencyLanding.heroPostPromoNote', { price: starterMo })}
          </div>
          <div style={{ marginTop: 22 }}>
            <ProofChips dark center items={agencyChips(t)} />
          </div>
        </div>
      </div>
      )}

      {/* ══ CAPABILITY STRIP ════════════════════════════════════════════════════
          Plain feature labels (icon + bold label + one short line), the same scale as the other feature text.
          Not stat counters: there are no numbers here, so it must not be styled like big KPI figures. */}
      <div data-testid="capability-strip" style={{ background: "#fff", borderBottom: `1px solid ${D.border}`, padding: isMobile ? "20px 16px" : "26px 48px", display: "flex", justifyContent: "center" }}>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? "14px 12px" : 20, maxWidth: 900, width: "100%" }}>
          {STATS.map((s) => (
            <li key={s.labelKey} style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
              <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 10, background: "#EEF2FF", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <s.Icon size={17} color={D.accent} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span data-testid="capability-label" style={{ display: "block", fontSize: 14, fontWeight: 800, color: D.navy, lineHeight: 1.3 }}>{t(s.valueKey)}</span>
                <span style={{ display: "block", fontSize: 12.5, color: D.textMid, marginTop: 2, lineHeight: 1.45 }}>{t(s.labelKey)}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* ══ DIFFERENTIATORS (mini-visual cards) ═══════════════════════════════ */}
      <div style={{ padding: sectionPad, background: "#fff" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", fontSize: isMobile ? 26 : 32, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 32px" }}>
            {t('agencyVisuals.diffTitle')}
          </h2>
          <DifferentialGrid />
        </div>
      </div>

      {/* ══ PRODUCT SHOWCASE ════════════════════════════════════════════════════ */}
      <div style={{ background: `linear-gradient(180deg, ${D.navy} 0%, #1a1f4a 100%)`, padding: isMobile ? "48px 12px 56px" : "64px 48px 80px" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 24 : 36 }}>
          <h2 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", margin: "0 0 8px" }}>
            {t('agencyLanding.lookInside')}
          </h2>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: 0 }}>{t('agencyLanding.lookInsideShort')}</p>
        </div>
        <ProductShowcase isMobile={isMobile} accent={D.accent} />
        <div style={{ maxWidth: 960, margin: isMobile ? "44px auto 0" : "64px auto 0" }}>
          <h3 style={{ textAlign: "center", fontSize: isMobile ? 20 : 24, fontWeight: 800, color: "#fff", margin: "0 0 28px" }}>{t('agencyLanding.flowTitle')}</h3>
          <FlowSteps dark isMobile={isMobile} steps={FLOW.map(f => ({ Icon: f.Icon, title: t(`agencyLanding.flow${f.n}Title`), sub: t(`agencyLanding.flow${f.n}Sub`) }))} />
        </div>
      </div>

      {/* ══ WE HELP FILL YOUR CASE ═════════════════════════════════════════════ */}
      <div style={{ padding: sectionPad, background: "#F8FAFC" }}>
        <div style={{ maxWidth: 1020, margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", fontSize: isMobile ? 26 : 32, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 32px" }}>{t('agencyVisuals.promoTitle')}</h2>
          <CasePromotion isMobile={isMobile} staffBcba />
          {!embedded && <OutsideFloridaNote isMobile={isMobile} />}
        </div>
      </div>

      {/* ══ ABACASES VS A GENERIC JOB BOARD ════════════════════════════════════ */}
      <div style={{ padding: sectionPad, background: "#fff", borderTop: `1px solid ${D.border}` }}>
        <div style={{ maxWidth: 1020, margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", fontSize: isMobile ? 26 : 32, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 28px" }}>{t('agencyVisuals.vsTitle')}</h2>
          <IndeedVsAbacases isMobile={isMobile} />
        </div>
      </div>

      {/* ══ PRICING ═════════════════════════════════════════════════════════════ */}
      <div id="pricing" data-sticky-cta-hide style={{ padding: sectionPad, background: "#F8FAFC" }}>
        <div style={{ maxWidth: 1020, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={{ fontSize: isMobile ? 28 : 34, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 8px" }}>
              {t('agencyLanding.pricingTitle')}
            </h2>
            <p style={{ fontSize: 15, color: D.textMid, margin: "0 0 20px" }}>{t('agencyLanding.pricingSubtitle')}</p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", border: `1.5px solid ${D.border}`, borderRadius: 100, padding: "6px 16px" }}>
              <button onClick={() => setAnnual(false)} style={{ padding: "5px 14px", borderRadius: 100, border: "none", background: !annual ? D.navy : "transparent", color: !annual ? "#fff" : D.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>{t('billing.monthly')}</button>
              <button onClick={() => setAnnual(true)} style={{ padding: "5px 14px", borderRadius: 100, border: "none", background: annual ? D.navy : "transparent", color: annual ? "#fff" : D.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                {t('agencyLanding.annualPro')}
                <span style={{ background: T.green, color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 100, padding: "2px 7px" }}>{t('billing.saveDiscount', 'SAVE {{pct}}%', { pct: annualDiscountPct })}</span>
              </button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 24, alignItems: "stretch", maxWidth: 720, margin: "0 auto" }}>
            {getPlans().map(plan => (
              <PriceCard key={plan.id} plan={plan} features={planFeatures[plan.id] || []} annual={annual} onSelect={onSignUp} extraSlotPrice={plan.id === "pro" ? slotPricePro : slotPriceStarter} breakEven={plan.id === "pro" ? proBreakEven : null} breakEvenAfter={plan.id === "pro" ? proBreakEvenAfter : null} />
            ))}
          </div>
          <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: D.textLight }}>{t('agencyLanding.pricingPostPromoFooter')}</p>
          <p data-testid="pricing-example" style={{ textAlign: "center", margin: "8px auto 0", maxWidth: 640, fontSize: 14, color: D.textMid, lineHeight: 1.6 }}>
            {t(promoActiveNow ? 'agencyLanding.pricingExamplePromo' : 'agencyLanding.pricingExample', {
              free: starterCases, endDate: endDateStrLocalized, startDate: startDateStrLocalized,
              nStarter: EXAMPLE_STARTER_OPENINGS, nPro: EXAMPLE_PRO_OPENINGS,
              starter: starterMo, sIncluded: starterIncluded, slot: slotPriceStarter,
              extraS: Math.max(0, EXAMPLE_STARTER_OPENINGS - starterIncluded),
              starterTotal: starterMo + Math.max(0, EXAMPLE_STARTER_OPENINGS - starterIncluded) * slotPriceStarter,
              pro: proMo, included: proCases, proSlot: slotPricePro,
              extraP: Math.max(0, EXAMPLE_PRO_OPENINGS - proCases),
              proTotal: proMo + Math.max(0, EXAMPLE_PRO_OPENINGS - proCases) * slotPricePro,
            })}
          </p>
          <p data-testid="pricing-volume" style={{ textAlign: "center", margin: "8px auto 0", fontSize: 14, color: D.textMid }}>
            {t('agencyLanding.volumeNote', { count: proCases })}{" "}
            <button type="button" onClick={() => setVolumeOpen(true)} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: D.navy, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>
              {t('agencyLanding.volumeLink')}
            </button>
          </p>
          <AgencyProPreview isMobile={isMobile} teamLogins={teamLogins} />
          <AgencyBoostsPreview isMobile={isMobile} config={platformConfig} />

        </div>
      </div>

      {/* ══ FOUNDING PARTNER ═══════════════════════════════════════════════════ */}
      <div style={{ padding: isMobile ? "48px 16px" : "64px 48px", background: "#fff", borderTop: `1px solid ${D.border}` }}>
        <PartnerCard isMobile={isMobile} onApply={() => setPartnerOpen(true)} />
      </div>

      {/* ══ FAQ ═════════════════════════════════════════════════════════════════ */}
      <div style={{ background: "#F8FAFC", borderTop: `1px solid ${D.border}`, padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", textAlign: "center", margin: "0 0 24px" }}>
            {t('agencyLanding.faqTitle')}
          </h2>
          {agencyFaqs.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={f.key} style={{ background: "#fff", border: `1px solid ${D.border}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
                <button
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: isMobile ? "16px 16px" : "17px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, fontFamily: "'Inter', sans-serif" }}
                >
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: D.navy }}>{t(`agencyLanding.${f.key}Q`)}</span>
                  <span aria-hidden="true" style={{ flex: "none", fontSize: 22, lineHeight: 1, color: D.textLight, transform: isOpen ? "rotate(45deg)" : "none", transition: "transform 0.18s ease" }}>+</span>
                </button>
                {isOpen && (
                  <div style={{ padding: isMobile ? "0 16px 16px" : "0 22px 18px", fontSize: 13.5, color: D.textMid, lineHeight: 1.7 }}>
                    {t(`agencyLanding.${f.key}A`, { ...faqVars, endDate: endDateStrLocalized, startDate: startDateStrLocalized, context: faqVars.isPromoActive ? undefined : "promoEnded" })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ FOUNDER ═════════════════════════════════════════════════════════════ */}
      <div style={{ padding: sectionPad, background: "#fff", borderTop: `1px solid ${D.border}` }}>
        <FounderCard isMobile={isMobile} onMessage={() => setContactOpen(true)} />
      </div>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      {volumeOpen && <ContactModal initialTopic="General" initialMessage={t('agencyLanding.volumePrefill', { count: proCases })} onClose={() => setVolumeOpen(false)} />}
      {partnerOpen && <ContactModal initialTopic="Partnership" initialMessage={t('contactModal.partnerPrefill')} onClose={() => setPartnerOpen(false)} />}
      {!embedded && <PublicFooter />}
      {!embedded && <StickyMobileCta label={promoActiveNow ? t('agencyPricing.cta') : t('agencyPricing.ctaPaid')} onClick={onSignUp} ctaId="agency" />}

    </div>
  );
}
